import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

/** A shimmering placeholder block (decorative: the loading state announces itself in text) */
export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div aria-hidden="true" className={cn('skeleton h-4', className)} {...props} />;
}

/*
 * Loading shapes that match the content to come (audit 10.8). Layout is inline styles on existing
 * classes, so these add nothing to the initial stylesheet. Each one announces `label` politely.
 */

const widths = ['72%', '48%', '60%', '36%', '54%', '42%'];
const w = (i: number): CSSProperties => ({ width: widths[i % widths.length] });

function Announce({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div role="status" aria-live="polite" className={className}>
      <span className="sr-only">{label}…</span>
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

/** A table: header row plus `rows` rows of `columns` cells, in a card like DataTable */
export function SkeletonTable({ rows = 6, columns = 4, label = 'Loading' }: { rows?: number; columns?: number; label?: string }) {
  const grid: CSSProperties = { display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 24, alignItems: 'center', padding: '0 16px' };
  return (
    <Announce label={label} className="table-scroll">
      <div className="bg-muted" style={{ ...grid, height: 44 }}>
        {Array.from({ length: columns }, (_, c) => (
          <div key={c} className="skeleton h-3.5" style={{ width: '40%' }} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="border-b border-border/70" style={{ ...grid, height: 48 }}>
          {Array.from({ length: columns }, (_, c) => (
            <div key={c} className="skeleton h-4" style={w(r + c)} />
          ))}
        </div>
      ))}
    </Announce>
  );
}

/** A list of cards: avatar, two lines, an action */
export function SkeletonList({ rows = 5, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <Announce label={label} className="card-list">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="card">
          <div className="skeleton h-9 w-9 rounded-full" />
          <div className="grid flex-1 gap-2">
            <div className="skeleton h-4" style={w(i)} />
            <div className="skeleton h-3.5" style={w(i + 3)} />
          </div>
          <div className="skeleton h-8 w-24 rounded-full" />
        </div>
      ))}
    </Announce>
  );
}

/** A pipeline board: columns of cards */
export function SkeletonBoard({ columns = 4, label = 'Loading' }: { columns?: number; label?: string }) {
  return (
    <Announce label={label} className="board">
      {Array.from({ length: columns }, (_, c) => (
        <div key={c} className="board-column">
          <div className="skeleton h-4" style={{ width: '50%' }} />
          <div className="board-cards">
            {Array.from({ length: 3 - (c % 2) }, (_, i) => (
              <div key={i} className="board-card">
                <div className="skeleton h-4" style={w(c + i)} />
                <div className="skeleton h-3.5" style={w(c + i + 2)} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </Announce>
  );
}

/** A record: summary header (avatar, name, facts) and two columns of cards */
export function SkeletonRecord({ label = 'Loading' }: { label?: string }) {
  return (
    <Announce label={label}>
      <div className="card flex items-center gap-4">
        <div className="skeleton h-14 w-14 rounded-full" />
        <div className="grid flex-1 gap-2">
          <div className="skeleton h-6" style={{ width: '40%' }} />
          <div className="skeleton h-4" style={{ width: '60%' }} />
        </div>
      </div>
      <div className="detail-grid">
        {[0, 1].map((col) => (
          <div key={col}>
            <div className="card grid gap-3">
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="skeleton h-4" style={w(i + col)} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Announce>
  );
}
